import { DocumentNode } from 'graphql'

import {
  ICompany,
  IDeliveryFieldsConfigurations,
  IDeliveryFollowingInput,
  IGenerateReportCommandInput,
  IMarkupInput,
  IMutation,
  IPermitType,
  IPermitTypeActionsDtoInput,
  IProjectMaterialInput,
  IRecurringWorkflowCreationResult,
  IScanHistory,
  IScanner,
  ISitePermitFollowingInput,
  ITeam,
  ITrade,
  IUserProject,
  IUserRole,
  NotificationType,
} from '~/client/graph'
import {
  ActivateCompaniesDocument,
  DeleteCompaniesDocument,
  LinkStruxHubCompanyDocument,
  MergeCompaniesDocument,
  SaveManyCompaniesDocument,
} from '~/client/graph/operations/generated/Companies.generated'
import { SaveThreadDocument } from '~/client/graph/operations/generated/ContentObjects.generated'
import {
  CreateDeliveryDocument,
  CreateRecurringDeliveriesDocument,
  DeleteManyDeliveriesDocument,
  UpdateDeliveryDocument,
  UpdateRecurringDeliveriesDocument,
} from '~/client/graph/operations/generated/Deliveries.generated'
import { SaveDeliveryConfigurationsDocument } from '~/client/graph/operations/generated/DeliveryConfigurations.generated'
import { SaveDeliveryFieldsConfigurationsDocument } from '~/client/graph/operations/generated/DeliveryFieldsConfigurations.generated'
import {
  DeleteManyDeliveryFollowingsDocument,
  SaveManyDeliveryFollowingsDocument,
} from '~/client/graph/operations/generated/DeliveryFollowing.generated'
import { SendSmsDeliveryTicketDocument } from '~/client/graph/operations/generated/DeliveryTickets.generated'
import { SaveLogisticsConfigurationsDocument } from '~/client/graph/operations/generated/LogisticsConfigurations.generated'
import {
  ISaveMaterialMutation,
  SaveMaterialDocument,
} from '~/client/graph/operations/generated/Materials.generated'
import { SaveMessageDocument } from '~/client/graph/operations/generated/Messages.generated'
import {
  NotifyEntityRepliedDocument,
  SaveManyNotificationsDocument,
  SaveNotificationDocument,
} from '~/client/graph/operations/generated/Notifications.generated'
import {
  ISavePermitTypesMutationVariables,
  SavePermitTypesDocument,
} from '~/client/graph/operations/generated/PermitTypes.generated'
import { SavePhotoDocument } from '~/client/graph/operations/generated/Photos.generated'
import { SaveProjectHistoryDocument } from '~/client/graph/operations/generated/ProjectHistory.generated'
import { GenerateReportDocument } from '~/client/graph/operations/generated/Reports.generated'
import { SaveManyScanHistoriesDocument } from '~/client/graph/operations/generated/ScanHistory.generated'
import {
  DeleteManyScannersDocument,
  DeleteScannerDocument,
  SaveManyScannersDocument,
} from '~/client/graph/operations/generated/Scanners.generated'
import {
  DeleteManySitePermitFollowingsDocument,
  SaveManySitePermitFollowingsDocument,
} from '~/client/graph/operations/generated/SitePermitFollow.generated'
import {
  AddSitePermitInspectionChangeDocument,
  AddSitePermitInspectionDocument,
} from '~/client/graph/operations/generated/SitePermitInspections.generated'
import {
  CreateRecurringWorkflowsDocument,
  SaveSitePermitsDocument,
} from '~/client/graph/operations/generated/SitePermits.generated'
import { SaveTeamDocument } from '~/client/graph/operations/generated/Teams.generated'
import {
  DeleteTradeDocument,
  SaveTradeDocument,
} from '~/client/graph/operations/generated/Trades.generated'
import { SaveManyUserProjectsDocument } from '~/client/graph/operations/generated/UserProjects.generated'
import { SaveUserRoleDocument } from '~/client/graph/operations/generated/UserRoles.generated'
import {
  SaveProjectMembersDocument,
  SendWelcomeEmailsDocument,
} from '~/client/graph/operations/generated/Users.generated'
import {
  DeleteMarkupDocument,
  SaveMarkupDocument,
} from '~/client/graph/operations/generated/WorkflowMarkups.generated'
import { SaveWorkflowsConfigurationsDocument } from '~/client/graph/operations/generated/WorkflowsConfigurations.generated'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Delivery from '~/client/src/shared/models/Delivery'
import { UserFacingError } from '~/client/src/shared/services/ApiAuthService'
import InitialState from '~/client/src/shared/stores/InitialState'
import RootStore from '~/client/src/shared/stores/Root.store'
import IDeliveryAttributeDto from '~/client/src/shared/types/IDeliveryAttributeDto'

import Message from '../../models/Message'
import BaseNotification from '../../models/Notification'
import PermitInspection from '../../models/PermitInspection'
import Photo from '../../models/Photo'
import RecurringDeliveriesSettings from '../../models/RecurringDeliveriesSettings'
import RecurringWorkflowSettings from '../../models/RecurringWorkflowSettings'
import Workflow, { IWorkflowDto } from '../../models/Workflow'
import { IServiceUserDto } from '../../types/UserDto'
import { mapToList } from '../../utils/converters'
import { handleEventCallback } from './commonEvents'
import * as e from './eventConstants'
import EventsStore from './Events.store'

export default function (eventsStore: EventsStore, rootStore: RootStore) {
  eventsStore.on(
    e.SAVE_PROJECT_TEAM,
    (state, team: ITeam, callback?: (id: string) => void) => {
      state.loading.set(e.SAVE_PROJECT_TEAM, true)

      return {
        graphMutation: {
          mutation: SaveTeamDocument,
          variables: {
            team,
          },
          onSuccess: [e.SAVE_PROJECT_TEAM_SUCCESS, callback as any],
          onError: [e.REQUEST_ERROR, e.SAVE_PROJECT_TEAM],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_PROJECT_TEAM_SUCCESS,
    (state, callback: (id: string) => void, { saveTeam }: IMutation) => {
      state.loading.set(e.SAVE_PROJECT_TEAM, false)

      return {
        dispatch: [e.RUN_CALLBACK, callback, saveTeam?.id],
      }
    },
  )

  eventsStore.on(
    e.SAVE_PROJECT_ROLE,
    (state, userRole: IUserRole, callback?: (id: string) => void) => {
      state.loading.set(e.SAVE_PROJECT_ROLE, true)

      return {
        graphMutation: {
          mutation: SaveUserRoleDocument,
          variables: {
            userRole,
          },
          onSuccess: [e.SAVE_PROJECT_ROLE_SUCCESS, callback as any],
          onError: [e.REQUEST_ERROR, e.SAVE_PROJECT_ROLE],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_PROJECT_ROLE_SUCCESS,
    (state, callback: (id: string) => void, { saveUserRole }: IMutation) => {
      state.loading.set(e.SAVE_PROJECT_ROLE, false)

      return {
        dispatch: [e.RUN_CALLBACK, callback, saveUserRole?.id],
      }
    },
  )

  eventsStore.on(
    e.CREATE_DELIVERY,
    (state, delivery, onSuccessCb, onErrorCb) => {
      state.loading.set(e.CREATE_DELIVERY, true)

      return {
        graphMutation: {
          mutation: CreateDeliveryDocument,
          variables: {
            delivery: delivery.getDto(state.activeProject.id),
            timezoneId: rootStore.projectDateStore.getClientTimezoneId(),
          },
          onSuccess: handleEventCallback(e.CREATE_DELIVERY, true, onSuccessCb),
          onError: handleEventCallback(e.CREATE_DELIVERY, true, onErrorCb),
        },
      }
    },
  )

  eventsStore.on(
    e.CREATE_RECURRING_DELIVERIES,
    (
      state,
      delivery: Delivery,
      recurringSettings: RecurringDeliveriesSettings,
      onSuccessCb,
      onErrorCb,
    ) => {
      state.loading.set(e.CREATE_RECURRING_DELIVERIES, true)

      return {
        graphMutation: {
          mutation: CreateRecurringDeliveriesDocument,
          variables: {
            delivery: {
              ...delivery.getDto(state.activeProject.id),
            },
            recurringSettings,
            timezoneId: rootStore.projectDateStore.getClientTimezoneId(),
          },
          onSuccess: handleEventCallback(
            e.CREATE_RECURRING_DELIVERIES,
            true,
            onSuccessCb,
          ),
          onError: handleEventCallback(
            e.CREATE_RECURRING_DELIVERIES,
            true,
            onErrorCb,
          ),
        },
      }
    },
  )

  eventsStore.on(
    e.UPDATE_DELIVERY,
    (state, delivery: Delivery, message: string, onSuccessCb, onErrorCb) => {
      state.loading.set(e.UPDATE_DELIVERY, true)

      return {
        graphMutation: {
          mutation: UpdateDeliveryDocument,
          variables: {
            delivery: delivery.getDto(state.activeProject.id),
            message,
            timezoneId: rootStore.projectDateStore.getClientTimezoneId(),
          },
          onSuccess: handleEventCallback(e.UPDATE_DELIVERY, true, onSuccessCb),
          onError: handleEventCallback(e.UPDATE_DELIVERY, true, onErrorCb),
        },
      }
    },
  )

  eventsStore.on(
    e.UPDATE_RECURRING_DELIVERIES,
    (
      state,
      delivery: Delivery,
      message: string,
      recurringSettings: RecurringDeliveriesSettings,
      relatedRecurringIds: number[],
      onSuccessCb,
      onErrorCb,
    ) => {
      state.loading.set(e.UPDATE_RECURRING_DELIVERIES, true)

      return {
        graphMutation: {
          mutation: UpdateRecurringDeliveriesDocument,
          variables: {
            delivery: delivery.getDto(state.activeProject.id),
            message,
            recurringSettings,
            relatedRecurringIds,
            timezoneId: rootStore.projectDateStore.getClientTimezoneId(),
          },
          onSuccess: handleEventCallback(
            e.UPDATE_RECURRING_DELIVERIES,
            true,
            onSuccessCb,
          ),
          onError: handleEventCallback(
            e.UPDATE_RECURRING_DELIVERIES,
            true,
            onErrorCb,
          ),
        },
      }
    },
  )

  eventsStore.on(e.DELETE_DELIVERIES, (state, ids: string[]) => {
    state.loading.set(e.DELETE_DELIVERIES, true)

    return {
      graphMutation: {
        mutation: DeleteManyDeliveriesDocument,
        variables: { ids },
        onSuccess: [e.COMPLETE_REQUEST, e.DELETE_DELIVERIES],
        onError: [e.REQUEST_ERROR, e.DELETE_DELIVERIES],
      },
    }
  })

  eventsStore.on(
    e.NOTIFY_THREAD_REPLIED,
    (state, entityId, notificationType: NotificationType) => {
      return {
        graphQuery: {
          query: NotifyEntityRepliedDocument,
          variables: {
            data: {
              projectId: state.activeProject.id,
              entityId,
              notificationType,
            },
          },
          onSuccess: [e.NO_EFFECT],
          onError: [e.REQUEST_ERROR, e.NOTIFY_THREAD_REPLIED],
        },
      }
    },
  )

  eventsStore.on(
    e.MARK_NOTIFICATION_AS_READ,
    (state, notification: BaseNotification) => {
      if (!notification || notification.wasRead) {
        return
      }

      state.loading.set(e.MARK_NOTIFICATION_AS_READ, true)
      notification.wasRead = true

      return {
        graphMutation: {
          mutation: SaveNotificationDocument,
          variables: {
            notification: notification.toDto(),
          },
          onSuccess: [e.COMPLETE_REQUEST, e.MARK_NOTIFICATION_AS_READ],
          onError: [e.REQUEST_ERROR, e.MARK_NOTIFICATION_AS_READ],
        },
      }
    },
  )

  eventsStore.on(
    e.MARK_NOTIFICATIONS_AS_READ,
    (
      _,
      notificationsToMarkAsRead: BaseNotification[],
      onSuccess,
      wasRead = true,
    ) => {
      if (notificationsToMarkAsRead.length === 0) {
        return
      }

      const notifications = notificationsToMarkAsRead.map(n => {
        n.wasRead = wasRead
        return n
      })

      return {
        graphMutation: {
          mutation: SaveManyNotificationsDocument,
          variables: { notifications: notifications.map(n => n.toDto()) },
          onSuccess: [e.RUN_CALLBACK, onSuccess],
          onError: [e.REQUEST_ERROR, e.MARK_NOTIFICATIONS_AS_READ],
        },
      }
    },
  )

  eventsStore.on(
    e.MARK_NOTIFICATIONS_AS_ARCHIVED,
    (
      _,
      archivingNotifications: BaseNotification[],
      onSuccess,
      isArchived = true,
    ) => {
      const notifications = archivingNotifications.map(n => {
        n.isArchived = isArchived
        return n
      })

      return {
        graphMutation: {
          mutation: SaveManyNotificationsDocument,
          variables: {
            notifications: notifications.map(n => n.toDto()),
          },
          onSuccess: [e.RUN_CALLBACK, onSuccess],
          onError: [e.REQUEST_ERROR, e.MARK_NOTIFICATIONS_AS_ARCHIVED],
        },
      }
    },
  )

  eventsStore.on(
    e.SEND_MESSAGE_TO_THREAD,
    (_, threadId: string, message: string) => {
      rootStore.slackBarStore.draftMessage = message
      rootStore.slackBarStore.replyOnThread(threadId)
    },
  )

  eventsStore.on(
    e.SAVE_WORKFLOWS,
    (
      state,
      workflows: Workflow[],
      message?: string,
      callbackFn?: (workflows: IWorkflowDto[]) => void,
      errorCallbackFn?: () => void,
    ) => {
      state.loading.set(e.SAVE_WORKFLOWS, true)

      return {
        graphMutation: {
          mutation: SaveSitePermitsDocument,
          variables: {
            permits: workflows.map(workflow => workflow.getDto(message)),
          },
          onSuccess: [e.SAVE_WORKFLOWS_SUCCESS, callbackFn as any],
          onError: errorCallbackFn
            ? [e.WORKFLOW_CREATE_FAILED, errorCallbackFn, e.SAVE_WORKFLOWS]
            : [e.REQUEST_ERROR, e.SAVE_WORKFLOWS],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_RECURRING_WORKFLOWS,
    (
      state,
      workflow: Workflow,
      recurringSettings: RecurringWorkflowSettings,
      callbackFn?: (result: IRecurringWorkflowCreationResult) => void,
      errorCallbackFn?: () => void,
    ) => {
      state.loading.set(e.SAVE_RECURRING_WORKFLOWS, true)

      return {
        graphMutation: {
          mutation: CreateRecurringWorkflowsDocument,
          variables: {
            workflow: workflow.getDto(),
            recurringSettings: recurringSettings,
            timezoneId: rootStore.projectDateStore.getClientTimezoneId(),
          },
          onSuccess: [e.SAVE_RECURRING_WORKFLOWS_SUCCESS, callbackFn as any],
          onError: errorCallbackFn
            ? [
                e.WORKFLOW_CREATE_FAILED,
                errorCallbackFn,
                e.SAVE_RECURRING_WORKFLOWS,
              ]
            : [e.REQUEST_ERROR, e.SAVE_RECURRING_WORKFLOWS],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_RECURRING_WORKFLOWS_SUCCESS,
    (
      state: InitialState,
      callbackFn: (result: IRecurringWorkflowCreationResult) => void,
      { createRecurringWorkflows }: IMutation,
    ) => {
      state.loading.set(e.SAVE_RECURRING_WORKFLOWS, false)

      callbackFn?.(createRecurringWorkflows)
    },
  )

  eventsStore.on(
    e.SAVE_WORKFLOWS_SUCCESS,
    (
      state: InitialState,
      callbackFn: (workflows: IWorkflowDto[]) => void,
      { saveManySitePermits }: IMutation,
    ) => {
      state.loading.set(e.SAVE_WORKFLOWS, false)
      state.lastSavedWorkflows = saveManySitePermits ?? []

      if (callbackFn) {
        callbackFn(saveManySitePermits)
      }
    },
  )

  // Re-dispatch REQUEST_ERROR so the failure is still reported after the caller's cleanup.
  eventsStore.on(
    e.WORKFLOW_CREATE_FAILED,
    (
      _state,
      errorCallbackFn: () => void,
      sourceEvent: string,
      error: unknown,
    ) => {
      errorCallbackFn?.()
      return { dispatchN: [[e.REQUEST_ERROR, sourceEvent, error]] }
    },
  )

  eventsStore.on(
    e.ADD_SITE_PERMIT_INSPECTION,
    (state, inspection: PermitInspection, onSuccessCb, onErrorCb) => {
      state.loading.set(e.ADD_SITE_PERMIT_INSPECTION, true)

      return {
        graphMutation: {
          mutation: AddSitePermitInspectionDocument,
          variables: {
            inspection: inspection.getDto(),
            fields: inspection.getFieldsDto(),
          },
          onSuccess: [e.ADD_SITE_PERMIT_INSPECTION_SUCCESS, onSuccessCb],
          onError: [e.RUN_CALLBACK, onErrorCb],
        },
      }
    },
  )

  eventsStore.on(
    e.ADD_SITE_PERMIT_INSPECTION_SUCCESS,
    (state: InitialState, callbackFn?) => {
      state.loading.set(e.ADD_SITE_PERMIT_INSPECTION, false)

      return {
        dispatch: [e.RUN_CALLBACK, callbackFn],
      }
    },
  )

  eventsStore.on(
    e.UPDATE_SITE_PERMIT_INSPECTION,
    (state, inspectionChange: PermitInspection, onSuccessCb, onErrorCb) => {
      state.loading.set(e.UPDATE_SITE_PERMIT_INSPECTION, true)

      return {
        graphMutation: {
          mutation: AddSitePermitInspectionChangeDocument,
          variables: {
            inspectionChange: inspectionChange.getDto(),
            fields: inspectionChange.getFieldsDto(),
          },
          onSuccess: [e.UPDATE_SITE_PERMIT_INSPECTION_SUCCESS, onSuccessCb],
          onError: [e.RUN_CALLBACK, onErrorCb],
        },
      }
    },
  )

  eventsStore.on(
    e.UPDATE_SITE_PERMIT_INSPECTION_SUCCESS,
    (state: InitialState, callbackFn?) => {
      state.loading.set(e.UPDATE_SITE_PERMIT_INSPECTION, false)

      return {
        dispatch: [e.RUN_CALLBACK, callbackFn],
      }
    },
  )

  eventsStore.on(e.SEND_WELCOME_EMAIL_TO_CURRENT_USER, state => {
    if (!state.user.email) {
      return
    }

    return {
      graphMutation: {
        mutation: SendWelcomeEmailsDocument,
        variables: {
          userEmails: [state.user.email],
          projectId: state.activeProject.id,
        },
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.SEND_WELCOME_EMAIL_TO_CURRENT_USER],
      },
    }
  })

  eventsStore.on(
    e.SAVE_DELIVERY_ATTRIBUTES,
    (state, attrs: [IDeliveryAttributeDto], mutation, cb) => {
      state.loading.set(e.SAVE_DELIVERY_ATTRIBUTES, true)

      return {
        graphMutation: {
          mutation,
          variables: { attrs },
          onSuccess: handleEventCallback(e.SAVE_DELIVERY_ATTRIBUTES, true, cb),
          onError: [e.REQUEST_ERROR, e.SAVE_DELIVERY_ATTRIBUTES],
        },
      }
    },
  )

  eventsStore.on(
    e.DELETE_DELIVERY_ATTRIBUTES,
    (state, ids: string[], mutation) => {
      state.loading.set(e.DELETE_DELIVERY_ATTRIBUTES, true)

      return {
        graphMutation: {
          mutation,
          variables: { ids },
          onSuccess: [e.COMPLETE_REQUEST, e.DELETE_DELIVERY_ATTRIBUTES],
          onError: [e.REQUEST_ERROR, e.DELETE_DELIVERY_ATTRIBUTES],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_MATERIAL,
    (
      state: InitialState,
      projectMaterial: IProjectMaterialInput,
      onSuccessCb: (result: ISaveMaterialMutation) => void,
      onErrorCb: (result: ISaveMaterialMutation) => void,
    ) => {
      state.loading.set(e.SAVE_MATERIAL, true)

      return {
        graphMutation: {
          mutation: SaveMaterialDocument,
          variables: { projectMaterial },
          onSuccess: [
            e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
            e.SAVE_MATERIAL,
            onSuccessCb,
          ],
          onError: [
            e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
            e.SAVE_MATERIAL,
            onErrorCb,
          ],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_SITE_PERMIT_FOLLOWINGS,
    (
      state,
      sitePermitFollowings: ISitePermitFollowingInput[],
      onSuccessCb?,
      onErrorCb?,
    ) => {
      if (!sitePermitFollowings.length) {
        return
      }

      state.loading.set(e.SAVE_SITE_PERMIT_FOLLOWINGS, true)

      return {
        graphMutation: {
          mutation: SaveManySitePermitFollowingsDocument,
          variables: { sitePermitFollowings },
          onSuccess: [
            e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
            e.SAVE_SITE_PERMIT_FOLLOWINGS,
            onSuccessCb,
          ],
          onError: [
            e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
            e.SAVE_SITE_PERMIT_FOLLOWINGS,
            onErrorCb,
          ],
        },
      }
    },
  )

  eventsStore.on(
    e.DELETE_SITE_PERMIT_FOLLOWINGS,
    (state, sitePermitFollowingIds: string[], onSuccessCb?, onErrorCb?) => {
      if (!sitePermitFollowingIds.length) {
        return
      }

      state.loading.set(e.DELETE_SITE_PERMIT_FOLLOWINGS, true)

      return {
        graphMutation: {
          mutation: DeleteManySitePermitFollowingsDocument,
          variables: { sitePermitFollowingIds },
          onSuccess: [
            e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
            e.DELETE_SITE_PERMIT_FOLLOWINGS,
            onSuccessCb,
          ],
          onError: [
            e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
            e.DELETE_SITE_PERMIT_FOLLOWINGS,
            onErrorCb,
          ],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_DELIVERY_FOLLOWINGS,
    (
      state,
      deliveryFollowings: IDeliveryFollowingInput[],
      onSuccessCb?,
      onErrorCb?,
    ) => {
      if (!deliveryFollowings.length) {
        return
      }

      state.loading.set(e.SAVE_DELIVERY_FOLLOWINGS, true)

      return {
        graphMutation: {
          mutation: SaveManyDeliveryFollowingsDocument,
          variables: { deliveryFollowings },
          onSuccess: [
            e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
            e.SAVE_DELIVERY_FOLLOWINGS,
            onSuccessCb,
          ],
          onError: [
            e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
            e.SAVE_DELIVERY_FOLLOWINGS,
            onErrorCb,
          ],
        },
      }
    },
  )

  eventsStore.on(
    e.DELETE_DELIVERY_FOLLOWINGS,
    (state, deliveryFollowingIds: string[], onSuccessCb?, onErrorCb?) => {
      if (!deliveryFollowingIds.length) {
        return
      }

      state.loading.set(e.DELETE_DELIVERY_FOLLOWINGS, true)

      return {
        graphMutation: {
          mutation: DeleteManyDeliveryFollowingsDocument,
          variables: { deliveryFollowingIds },
          onSuccess: [
            e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
            e.DELETE_DELIVERY_FOLLOWINGS,
            onSuccessCb,
          ],
          onError: [
            e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
            e.DELETE_DELIVERY_FOLLOWINGS,
            onErrorCb,
          ],
        },
      }
    },
  )

  eventsStore.on(e.REQUEST_RESET_PASSWORD, (_, email, onSuccess, onError) => {
    return {
      cmd: {
        cmd: {
          execute: () => {
            return rootStore.auth.resetPassword(email)
          },
        },
        onSuccess: [e.RUN_CALLBACK, onSuccess],
        onError: [e.RESET_PASSWORD_ERROR, onError],
      },
    }
  })

  eventsStore.on(
    e.RESET_PASSWORD_ERROR,
    (_, onErrorCb: (message: string) => any, error: Error) => {
      onErrorCb?.(
        error instanceof UserFacingError
          ? error.message
          : Localization.translator.somethingWentWrongPleaseTryAgain,
      )
    },
  )

  eventsStore.on(
    e.SAVE_ASSIGNMENTS,
    (
      state,
      mutationDocument: DocumentNode,
      assignments: unknown[],
      cb?: (message: string) => void,
    ) => {
      state.loading.set(e.SAVE_ASSIGNMENTS, true)

      return {
        graphMutation: {
          mutation: mutationDocument,
          variables: {
            assignments,
          },
          onSuccess: [e.SAVE_ASSIGNMENTS_SUCCESS, cb as any],
          onError: [e.REQUEST_ERROR, e.SAVE_ASSIGNMENTS],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_ASSIGNMENTS_SUCCESS,
    (state, cb?: (message: string) => void) => {
      state.loading.set(e.SAVE_ASSIGNMENTS, false)

      return {
        dispatch: [e.RUN_CALLBACK, cb],
      }
    },
  )

  eventsStore.on(
    e.DELETE_ASSIGNMENTS,
    (
      state,
      mutationDocument: DocumentNode,
      ids: string[],
      cb?: (message: string) => void,
    ) => {
      state.loading.set(e.DELETE_ASSIGNMENTS, true)

      return {
        graphMutation: {
          mutation: mutationDocument,
          variables: {
            ids,
          },
          onSuccess: [e.DELETE_ASSIGNMENTS_SUCCESS, cb as any],
          onError: [e.REQUEST_ERROR, e.DELETE_ASSIGNMENTS],
        },
      }
    },
  )

  eventsStore.on(
    e.DELETE_ASSIGNMENTS_SUCCESS,
    (state, cb?: (message: string) => void) => {
      state.loading.set(e.DELETE_ASSIGNMENTS, false)

      return {
        dispatch: [e.RUN_CALLBACK, cb],
      }
    },
  )

  eventsStore.on(
    e.SAVE_PROJECT_TRADE,
    (state, trade: ITrade, callback?: (id: string) => void) => {
      state.loading.set(e.SAVE_PROJECT_TRADE, true)

      return {
        graphMutation: {
          mutation: SaveTradeDocument,
          variables: {
            trade,
          },
          onSuccess: [e.SAVE_PROJECT_TRADE_SUCCESS, callback as any],
          onError: [e.REQUEST_ERROR, e.SAVE_PROJECT_TRADE],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_PROJECT_TRADE_SUCCESS,
    (state, callback: (id: string) => void, { saveTrade }: IMutation) => {
      state.loading.set(e.SAVE_PROJECT_TRADE, false)

      return {
        dispatch: [e.RUN_CALLBACK, callback, saveTrade?.id],
      }
    },
  )

  eventsStore.on(
    e.DELETE_PROJECT_TRADE,
    (state, tradeId: string, callback?: (id: string) => void) => {
      state.loading.set(e.DELETE_PROJECT_TRADE, true)

      return {
        graphMutation: {
          mutation: DeleteTradeDocument,
          variables: { tradeId },
          onSuccess: [e.DELETE_PROJECT_TRADE_SUCCESS, tradeId as any, callback],
          onError: [e.REQUEST_ERROR, e.DELETE_PROJECT_TRADE],
        },
      }
    },
  )

  eventsStore.on(
    e.DELETE_PROJECT_TRADE_SUCCESS,
    (state, tradeId: string, callback?: (id: string) => void) => {
      state.loading.set(e.DELETE_PROJECT_TRADE, false)

      return {
        dispatch: [e.RUN_CALLBACK, callback, tradeId],
      }
    },
  )

  eventsStore.on(
    e.SAVE_DELIVERY_CONFIGURATIONS,
    (state, configurations, onSuccessCb, onErrorCb) => {
      state.loading.set(e.SAVE_DELIVERY_CONFIGURATIONS, true)

      const deliveryConfigurations = Object.assign(
        state.delivery.configurations,
        configurations,
      )

      return {
        graphMutation: {
          mutation: SaveDeliveryConfigurationsDocument,
          variables: { deliveryConfigurations },
          onSuccess: [
            e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
            e.SAVE_DELIVERY_CONFIGURATIONS,
            onSuccessCb,
          ],
          onError: [
            e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
            e.SAVE_DELIVERY_CONFIGURATIONS,
            onErrorCb,
          ],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_LOGISTICS_CONFIGURATIONS,
    (state, configurations, onSuccessCb, onErrorCb) => {
      const logisticsConfigurations = Object.assign(
        state.logistics.configurations,
        configurations,
      )
      return {
        graphMutation: {
          mutation: SaveLogisticsConfigurationsDocument,
          variables: { logisticsConfigurations },
          onSuccess: [e.RUN_CALLBACK, onSuccessCb],
          onError: [e.RUN_CALLBACK, onErrorCb],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_WORKFLOWS_CONFIGURATIONS,
    (state, configurations, onSuccessCb, onErrorCb) => {
      const workflowsConfigurations = Object.assign(
        state.workflows.configurations,
        configurations,
      )
      return {
        graphMutation: {
          mutation: SaveWorkflowsConfigurationsDocument,
          variables: { workflowsConfigurations },
          onSuccess: [e.RUN_CALLBACK, onSuccessCb],
          onError: [e.RUN_CALLBACK, onErrorCb],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_DELIVERY_FIELDS_CONFIGURATIONS,
    (state, onSuccessCb, onErrorCb) => {
      state.loading.set(e.SAVE_DELIVERY_FIELDS_CONFIGURATIONS, true)
      const {
        mandatoryFields,
        hiddenFields,
        customFieldNames,
        deliveryFieldsConfigId: configId,
      } = state.delivery

      const deliveryFieldsConfigurations: IDeliveryFieldsConfigurations = {
        id: configId,
        projectId: state.activeProject.id,
        mandatoryFields: mapToList(mandatoryFields, id => mandatoryFields[id]),
        hiddenFields: mapToList(hiddenFields, id => hiddenFields[id]),
        fieldNames: mapToList(customFieldNames, undefined, (id, name) => ({
          id,
          name,
        })),
      }

      return {
        graphMutation: {
          mutation: SaveDeliveryFieldsConfigurationsDocument,
          variables: { deliveryFieldsConfigurations },
          onSuccess: [
            e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
            e.SAVE_DELIVERY_FIELDS_CONFIGURATIONS,
            onSuccessCb,
          ],
          onError: [
            e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
            e.SAVE_DELIVERY_FIELDS_CONFIGURATIONS,
            onErrorCb,
          ],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_PROJECT_HISTORY,
    (state, projectHistory, onSuccessCb, onErrorCb, shouldCompleteRequest) => {
      state.loading.set(e.SAVE_PROJECT_HISTORY, true)
      return {
        graphMutation: {
          mutation: SaveProjectHistoryDocument,
          variables: { projectHistory },
          onSuccess: handleEventCallback(
            e.SAVE_PROJECT_HISTORY,
            shouldCompleteRequest,
            onSuccessCb,
          ),
          onError: handleEventCallback(
            e.SAVE_PROJECT_HISTORY,
            shouldCompleteRequest,
            onErrorCb,
          ),
        },
      }
    },
  )

  eventsStore.on(e.SAVE_THREAD, (_, thread, onSuccessCb, onErrorCb) => {
    return {
      graphMutation: {
        mutation: SaveThreadDocument,
        variables: { thread },
        onSuccess: [e.RUN_CALLBACK, onSuccessCb],
        onError: [e.RUN_CALLBACK, onErrorCb],
      },
    }
  })

  eventsStore.on(
    e.SAVE_MESSAGE,
    (state, data: Message, onSuccessCb, onErrorCb) => {
      state.loading.set(e.SAVE_MESSAGE, true)

      return {
        graphMutation: {
          mutation: SaveMessageDocument,
          variables: { message: data.getDto() },
          onSuccess: [
            e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
            e.SAVE_MESSAGE,
            onSuccessCb,
          ],
          onError: [
            e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
            e.SAVE_MESSAGE,
            onErrorCb,
          ],
        },
      }
    },
  )

  eventsStore.on(e.SAVE_PHOTO, (_, data: Photo, onSuccessCb, onErrorCb) => {
    return {
      graphMutation: {
        mutation: SavePhotoDocument,
        variables: { photo: data },
        onSuccess: [e.RUN_CALLBACK, onSuccessCb],
        onError: [e.RUN_CALLBACK, onErrorCb],
      },
    }
  })

  eventsStore.on(
    e.SAVE_SCANNERS,
    (state: InitialState, scanners: IScanner[], onSuccessCb?, onErrorCb?) => {
      state.loading.set(e.SAVE_SCANNERS, true)

      return {
        graphMutation: {
          mutation: SaveManyScannersDocument,
          variables: {
            scanners,
          },
          onSuccess: [e.SAVE_SCANNERS_SUCCESS, onSuccessCb],
          onError: handleEventCallback(e.SAVE_SCANNERS, true, onErrorCb),
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_SCANNERS_SUCCESS,
    (state: InitialState, callbackFn, { saveManyScanners }: IMutation) => {
      state.loading.set(e.SAVE_SCANNERS, false)

      return {
        dispatch: [e.RUN_CALLBACK, callbackFn, saveManyScanners],
      }
    },
  )

  eventsStore.on(
    e.SAVE_SCAN_HISTORIES,
    (
      state: InitialState,
      scanHistories: IScanHistory[],
      onSuccessCb?,
      onErrorCb?,
    ) => {
      state.loading.set(e.SAVE_SCAN_HISTORIES, true)

      return {
        graphMutation: {
          mutation: SaveManyScanHistoriesDocument,
          variables: {
            scanHistories,
          },
          onSuccess: [e.SAVE_SCAN_HISTORIES_SUCCESS, onSuccessCb],
          onError: handleEventCallback(e.SAVE_SCAN_HISTORIES, true, onErrorCb),
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_SCAN_HISTORIES_SUCCESS,
    (state: InitialState, callbackFn, { saveManyScanHistories }: IMutation) => {
      state.loading.set(e.SAVE_SCAN_HISTORIES, false)

      return {
        dispatch: [e.RUN_CALLBACK, callbackFn, saveManyScanHistories],
      }
    },
  )

  eventsStore.on(
    e.DELETE_SCANNER,
    (state: InitialState, scannerId: string, onSuccessCb, onErrorCb) => {
      state.loading.set(e.DELETE_SCANNER, true)

      return {
        graphMutation: {
          mutation: DeleteScannerDocument,
          variables: { scannerId },
          onSuccess: handleEventCallback(e.DELETE_SCANNER, true, onSuccessCb),
          onError: handleEventCallback(e.DELETE_SCANNER, true, onErrorCb),
        },
      }
    },
  )

  eventsStore.on(
    e.DELETE_SCANNERS,
    (state: InitialState, scannerIds: string[], onSuccessCb, onErrorCb) => {
      state.loading.set(e.DELETE_SCANNERS, true)

      return {
        graphMutation: {
          mutation: DeleteManyScannersDocument,
          variables: { scannerIds },
          onSuccess: handleEventCallback(e.DELETE_SCANNERS, true, onSuccessCb),
          onError: handleEventCallback(e.DELETE_SCANNERS, true, onErrorCb),
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_COMPANIES,
    (state: InitialState, companies: ICompany[], onSuccessCb?, onErrorCb?) => {
      state.loading.set(e.SAVE_COMPANIES, true)

      return {
        graphMutation: {
          mutation: SaveManyCompaniesDocument,
          variables: {
            companies,
          },
          onSuccess: [e.SAVE_COMPANIES_SUCCESS, onSuccessCb],
          onError: handleEventCallback(e.SAVE_COMPANIES, true, onErrorCb),
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_COMPANIES_SUCCESS,
    (state: InitialState, callbackFn, { saveManyCompanies }: IMutation) => {
      state.loading.set(e.SAVE_COMPANIES, false)

      return {
        dispatch: [e.RUN_CALLBACK, callbackFn, saveManyCompanies],
      }
    },
  )

  eventsStore.on(
    e.SAVE_MEMBERS,
    (
      state: InitialState,
      members: IServiceUserDto[],
      reassignClaimedPhone?: boolean,
      onSuccessCb?,
      onErrorCb?,
    ) => {
      state.loading.set(e.SAVE_MEMBERS, true)

      return {
        graphMutation: {
          mutation: SaveProjectMembersDocument,
          variables: {
            members,
            reassignClaimedPhone,
          },
          onSuccess: [
            e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
            e.SAVE_MEMBERS,
            onSuccessCb,
          ],
          onError: [
            e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
            e.SAVE_MEMBERS,
            onErrorCb,
          ],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_USER_PROJECTS,
    (
      state: InitialState,
      userProjects: IUserProject[],
      onSuccessCb?,
      onErrorCb?,
    ) => {
      state.loading.set(e.SAVE_USER_PROJECTS, true)

      return {
        graphMutation: {
          mutation: SaveManyUserProjectsDocument,
          variables: {
            userProjects,
          },
          onSuccess: [e.SAVE_USER_PROJECTS_SUCCESS, onSuccessCb],
          onError: [e.SAVE_USER_PROJECTS_ERROR, onErrorCb],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_USER_PROJECTS_SUCCESS,
    (state: InitialState, callbackFn, { saveManyUserProjects }: IMutation) => {
      state.loading.set(e.SAVE_USER_PROJECTS, false)

      return {
        dispatch: [e.RUN_CALLBACK, callbackFn, saveManyUserProjects],
      }
    },
  )

  eventsStore.on(e.SAVE_USER_PROJECTS_ERROR, (_: InitialState, callbackFn?) => {
    return {
      dispatchN: [
        [e.RUN_CALLBACK, callbackFn],
        [e.REQUEST_ERROR, e.SAVE_USER_PROJECTS],
      ],
    }
  })

  eventsStore.on(
    e.DELETE_COMPANIES,
    (state: InitialState, companyIds: string[], onSuccessCb, onErrorCb) => {
      state.loading.set(e.DELETE_COMPANIES, true)

      return {
        graphMutation: {
          mutation: DeleteCompaniesDocument,
          variables: { companyIds },
          onSuccess: handleEventCallback(e.DELETE_COMPANIES, true, onSuccessCb),
          onError: handleEventCallback(e.DELETE_COMPANIES, true, onErrorCb),
        },
      }
    },
  )

  eventsStore.on(
    e.ACTIVATE_COMPANIES,
    (state: InitialState, companyIds: string[], onSuccessCb, onErrorCb) => {
      state.loading.set(e.ACTIVATE_COMPANIES, true)

      return {
        graphMutation: {
          mutation: ActivateCompaniesDocument,
          variables: { companyIds },
          onSuccess: handleEventCallback(
            e.ACTIVATE_COMPANIES,
            true,
            onSuccessCb,
          ),
          onError: handleEventCallback(e.ACTIVATE_COMPANIES, true, onErrorCb),
        },
      }
    },
  )

  eventsStore.on(
    e.MERGE_COMPANIES,
    (
      state: InitialState,
      companyIds: string[],
      targetCompany: ICompany,
      onSuccessCb,
      onErrorCb,
    ) => {
      state.loading.set(e.MERGE_COMPANIES, true)

      return {
        graphMutation: {
          mutation: MergeCompaniesDocument,
          variables: { companyIds, targetCompany },
          onSuccess: handleEventCallback(e.MERGE_COMPANIES, true, onSuccessCb),
          onError: handleEventCallback(e.MERGE_COMPANIES, true, onErrorCb),
        },
      }
    },
  )

  eventsStore.on(
    e.LINK_STRUXHUB_COMPANY,
    (
      state: InitialState,
      companyId: string,
      struxHubCompanyId: string,
      onSuccessCb,
      onErrorCb,
    ) => {
      state.loading.set(e.LINK_STRUXHUB_COMPANY, true)

      return {
        graphMutation: {
          mutation: LinkStruxHubCompanyDocument,
          variables: { companyId, struxHubCompanyId },
          onSuccess: handleEventCallback(
            e.LINK_STRUXHUB_COMPANY,
            true,
            onSuccessCb,
          ),
          onError: handleEventCallback(
            e.LINK_STRUXHUB_COMPANY,
            true,
            onErrorCb,
          ),
        },
      }
    },
  )

  eventsStore.on(
    e.SEND_SMS_DELIVERY_TICKET,
    (state: InitialState, deliveryId: string, onErrorCb) => {
      state.loading.set(e.SEND_SMS_DELIVERY_TICKET, true)
      return {
        graphMutation: {
          mutation: SendSmsDeliveryTicketDocument,
          variables: {
            deliveryId,
          },
          onError: [
            e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
            e.SEND_SMS_DELIVERY_TICKET,
            onErrorCb,
          ],
          onSuccess: [e.SEND_SMS_DELIVERY_TICKET_SUCCESS],
        },
      }
    },
  )

  eventsStore.on(e.SEND_SMS_DELIVERY_TICKET_SUCCESS, (state: InitialState) => {
    state.loading.set(e.SEND_SMS_DELIVERY_TICKET, false)
  })

  eventsStore.on(e.SHOW_FULLSCREEN_PREVIEW, () => {
    // only postEventCallback is needed
  })

  eventsStore.on(
    e.DOWNLOAD_REPORT_FILE,
    (
      _: InitialState,
      generateReportInput: IGenerateReportCommandInput,
      callback?,
      errorCallback?: () => void,
    ) => {
      return {
        graphMutation: {
          mutation: GenerateReportDocument,
          variables: { generateReportInput },
          onSuccess: [
            e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
            e.DOWNLOAD_REPORT_SUCCESS,
            callback,
          ],
          onError: [e.DOWNLOAD_REPORT_ERROR, errorCallback],
        },
      }
    },
  )

  eventsStore.on(
    e.DOWNLOAD_REPORT_ERROR,
    (_, errorCallback?: () => void, error?: unknown) => {
      errorCallback?.()
      return { dispatch: [e.REPORT_ERROR, e.DOWNLOAD_REPORT_FILE, error] }
    },
  )

  eventsStore.on(
    e.SAVE_MARKUP,
    (
      state,
      markup: IMarkupInput,
      callbackFn?: (id: string) => void,
      errorCallbackFn?: (error: unknown) => void,
    ) => {
      state.loading.set(e.SAVE_MARKUP, true)

      return {
        graphMutation: {
          mutation: SaveMarkupDocument,
          variables: {
            markup,
          },
          onSuccess: [e.SAVE_MARKUP_SUCCESS, callbackFn as any],
          onError: [e.SAVE_MARKUP_FAILURE, errorCallbackFn as any],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_MARKUP_SUCCESS,
    (state, callbackFn: (id: string) => void, { saveMarkup }: IMutation) => {
      state.loading.set(e.SAVE_MARKUP, false)

      return {
        dispatch: [e.RUN_CALLBACK, callbackFn, saveMarkup?.id],
      }
    },
  )

  eventsStore.on(
    e.SAVE_MARKUP_FAILURE,
    (state, errorCallbackFn: (error: unknown) => void, error: unknown) => {
      state.loading.set(e.SAVE_MARKUP, false)
      return {
        dispatchN: [
          [e.REQUEST_ERROR, e.SAVE_MARKUP, error],
          [e.RUN_CALLBACK, errorCallbackFn, error],
        ],
      }
    },
  )

  eventsStore.on(
    e.DELETE_MARKUP,
    (
      state,
      id: string,
      callbackFn?: () => void,
      errorCallbackFn?: (error: unknown) => void,
    ) => {
      return {
        graphMutation: {
          mutation: DeleteMarkupDocument,
          variables: { id },
          onSuccess: [e.RUN_CALLBACK, callbackFn as any],
          onError: [e.DELETE_MARKUP_FAILURE, errorCallbackFn as any],
        },
      }
    },
  )

  eventsStore.on(
    e.DELETE_MARKUP_FAILURE,
    (state, errorCallbackFn: (error: unknown) => void, error: unknown) => {
      return {
        dispatchN: [
          [e.REQUEST_ERROR, e.DELETE_MARKUP, error],
          [e.RUN_CALLBACK, errorCallbackFn, error],
        ],
      }
    },
  )

  // STX1-1057: moved here from desktopChangeEvents so the mobile bundle can
  // dispatch it too — the set-password prompt is shown on both.
  // STX1-366: users invited with email+phone can land on profile without a
  // password (skipped the invite-link prompt, or signed in via phone OTP).
  // SET_PASSWORD hits auth's /set-password, which attaches password + phone
  // atomically so the Firebase record reaches [password, phone].
  eventsStore.on(
    e.SET_PASSWORD,
    (state, newPassword, onSuccessCb, onErrorCb) => {
      state.loading.set(e.SET_PASSWORD, true)

      const { auth } = rootStore
      return {
        cmd: {
          cmd: {
            execute: () => auth.setPassword(newPassword),
          },
          // The outcome rides along with the dispatch instead of being read
          // back off the payload — see SET_PASSWORD_RESULT below.
          onSuccess: [e.SET_PASSWORD_RESULT, onSuccessCb, true],
          onError: [e.SET_PASSWORD_RESULT, onErrorCb, false],
        },
      }
    },
  )

  eventsStore.on(
    e.SET_PASSWORD_RESULT,
    (
      state,
      callback: (error?: Error) => any,
      isSuccess: boolean,
      resultOrError?: unknown,
    ) => {
      state.loading.set(e.SET_PASSWORD, false)

      // Which outcome this is comes from the dispatch that raised it, never
      // from the runtime type of the payload. The 30s bound aborts the request,
      // and an abort rejects with a DOMException whose Error inheritance is
      // realm-dependent — under jsdom it has none — so an `instanceof Error`
      // read of it classifies a save that never happened as one that did.
      // The processor appends the resolved value or the rejection after the
      // arguments named here, so the payload stays available either way.
      if (isSuccess) {
        // auth.hasPassword comes from /auth/me and nothing else updates it, so
        // after a successful write every gate reading it — this prompt,
        // Profile's password section — is working from a stale false until it
        // is refreshed.
        void rootStore.auth.loadAuthMe()
      } else if (
        (resultOrError as { code?: string } | undefined)?.code ===
        'invalid_current_password'
      ) {
        // A first-time set refused for want of a current password means one was
        // set elsewhere (the portal, another tab) since /me loaded: refresh so
        // the password section switches to change mode.
        void rootStore.auth.loadAuthMe()
      }

      if (callback) {
        // Failures are handed on as they arrived; both callers read .message.
        callback(isSuccess ? null : (resultOrError as Error))
      }
    },
  )

  // Shared rather than desktop-only: the resource card's Workflow Types control commits
  // field allowances through this save, and that card renders on mobile too.
  eventsStore.on(
    e.SAVE_WORKFLOW_TYPES,
    (
      state: InitialState,
      workflowTypes: IPermitType[],
      actions: IPermitTypeActionsDtoInput[],
      callbackFn?,
      errorCallbackFn?: (error: unknown) => void,
      ruleChanges?: Pick<
        ISavePermitTypesMutationVariables,
        'operationRules' | 'deletedOperationRuleIds'
      >,
    ) => {
      state.loading.set(e.SAVE_WORKFLOW_TYPES, true)

      return {
        graphMutation: {
          mutation: SavePermitTypesDocument,
          variables: {
            permitTypes: workflowTypes,
            actions,
            projectId: state.activeProject.id,
            ...ruleChanges,
          },
          onSuccess: [e.SAVE_WORKFLOW_TYPES_SUCCESS, callbackFn],
          // Without an error callback a failed save is silent: callbackFn never fires, so a
          // caller awaiting the write would wait forever. Callers that need to know pass one.
          onError: errorCallbackFn
            ? [
                e.SAVE_WORKFLOW_TYPES_FAILED,
                errorCallbackFn,
                e.SAVE_WORKFLOW_TYPES,
              ]
            : [e.REQUEST_ERROR, e.SAVE_WORKFLOW_TYPES],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_WORKFLOW_TYPES_FAILED,
    (
      state: InitialState,
      errorCallbackFn: (error: unknown) => void,
      sourceEvent: string,
      error: unknown,
    ) => {
      state.loading.set(e.SAVE_WORKFLOW_TYPES, false)
      errorCallbackFn?.(error)
      return { dispatchN: [[e.REQUEST_ERROR, sourceEvent, error]] }
    },
  )

  eventsStore.on(
    e.SAVE_WORKFLOW_TYPES_SUCCESS,
    (state: InitialState, callbackFn, { saveManyPermitTypes }: IMutation) => {
      state.loading.set(e.SAVE_WORKFLOW_TYPES, false)

      saveManyPermitTypes?.forEach(type =>
        rootStore.workflowTypesStore.receiveProjectSpecificType(type.id, type),
      )

      if (callbackFn) {
        callbackFn(saveManyPermitTypes)
      }
    },
  )
}
